"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DealPriceAdjustmentSchema = exports.PackagePriceChangeSchema = void 0;
exports.dealPriceAdjustments = dealPriceAdjustments;
const zod_1 = require("zod");
/**
 * PACKAGE PRICE PROPAGATION — Task 1 of the price-propagation plan.
 *
 * The product owner's rule, in his own words: "package price is X. for this
 * package deal was discount $10. now X -> X+50. new tenant price should be
 * X+50-$10." A deal's promise is its DISCOUNT off the package's list price,
 * never an absolute number — so when a package's list price changes, every
 * deal priced off that package must move its stored `priceCents` by the
 * SAME delta the package moved by. Skip that and a brand-new signup on the
 * deal would pay a different price than an existing tenant already on it,
 * for the exact same discount.
 *
 * Worked example (also the plan's own): Core rises 3999 -> 5999 (delta
 * +2000). Deal STARTER-2024 stores `priceCents: 2999`, so it must become
 * `4999` — after which a new signup and an existing tenant both pay 4999.
 *
 * This module is PURE: no I/O, no clock, no side effects. It only computes
 * which deals must move and by how much; a later task performs the actual
 * write (and that later task is where money changes hands, not here).
 */
exports.PackagePriceChangeSchema = zod_1.z.object({
    packageId: zod_1.z.string().min(1),
    fromCents: zod_1.z.number().int(),
    toCents: zod_1.z.number().int(),
});
exports.DealPriceAdjustmentSchema = zod_1.z.object({
    dealId: zod_1.z.string().min(1),
    dealCode: zod_1.z.string().min(1),
    fromPriceCents: zod_1.z.number().int().nonnegative(),
    toPriceCents: zod_1.z.number().int().nonnegative(),
    deltaCents: zod_1.z.number().int(),
});
/**
 * Which deals must move their `priceCents`, and by how much. Pure — no I/O,
 * no clock, changes nobody's bill (it only computes the adjustment; a
 * separate task applies it).
 *
 * Rules encoded here (see the test matrix for the exhaustive list):
 * - A deal with no `priceCents` at all is ABSENT from the result — never a
 *   zero-delta row. `priceCents: 0` is a real free deal and IS present:
 *   checked with `typeof d.priceCents === 'number'`, never truthiness.
 * - Each package a deal references contributes its own delta; a deal
 *   referencing two changed packages moves by the SUM of both deltas.
 * - A package id repeated within one deal's `packageIds` counts ONCE — the
 *   plan's own §2 text ("delta applies once per occurrence") conflicts with
 *   its Task 1 test matrix ("duplicate package id ... counted once, be
 *   defensive, assert it"); this function follows the test matrix and
 *   dedupes defensively rather than trusting the schema-level uniqueness
 *   invariant to always hold upstream.
 * - A negative delta (a price CUT) moves `toPriceCents` down, uncapped,
 *   EXCEPT it is floored at 0 if the raw arithmetic would otherwise go
 *   negative — a price can never be reported as negative cents.
 * - INVARIANT: `fromPriceCents + deltaCents === toPriceCents`, always.
 *   `deltaCents` is therefore the ACTUAL movement (`toPriceCents -
 *   fromPriceCents`), never the raw/unclamped sum of package deltas — on
 *   the floored path those two differ (e.g. `fromPriceCents: 100`, raw sum
 *   of package deltas `-500`, which would drive the price negative, so
 *   `toPriceCents` floors to `0` and the reported `deltaCents` is `-100`,
 *   not `-500`). Reporting the raw sum instead would let a caller that
 *   trusts `fromPriceCents + deltaCents` rather than `toPriceCents` compute
 *   a NEGATIVE price.
 */
function dealPriceAdjustments(deals, changes) {
    const deltaByPackageId = new Map();
    for (const c of changes) {
        deltaByPackageId.set(c.packageId, c.toCents - c.fromCents);
    }
    const adjustments = [];
    for (const deal of deals) {
        if (typeof deal.priceCents !== 'number')
            continue; // absent, not a zero-delta row
        const uniquePackageIds = new Set(deal.packageIds ?? []); // dedupe: count once per unique package id
        let totalDelta = 0;
        let matchedAnyPackage = false;
        for (const packageId of uniquePackageIds) {
            const delta = deltaByPackageId.get(packageId);
            if (delta !== undefined) {
                totalDelta += delta;
                matchedAnyPackage = true;
            }
        }
        // Absent when no changed package touches this deal at all — but a deal
        // that DOES reference changed packages whose deltas happen to cancel to
        // a net 0 is still a real occurrence and stays in the result.
        if (!matchedAnyPackage)
            continue;
        const fromPriceCents = deal.priceCents;
        const toPriceCents = Math.max(0, fromPriceCents + totalDelta);
        adjustments.push({
            dealId: deal.id,
            dealCode: deal.code,
            fromPriceCents,
            toPriceCents,
            // Actual movement, not the raw/unclamped sum — keeps the invariant
            // `fromPriceCents + deltaCents === toPriceCents` true on the floored
            // path too (see the doc comment above).
            deltaCents: toPriceCents - fromPriceCents,
        });
    }
    return adjustments;
}
//# sourceMappingURL=pricePropagation.js.map